import { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { featuredVideos } from '@/mocks/videos';
import PaymentModal from '@/components/feature/PaymentModal';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';
const TRIAL_VIDEO_URL = 'https://www.w3schools.com/html/mov_bbb.mp4';
const TRIAL_LIMIT_SECONDS = 180;

interface VideoPlayerProps {
  purchased: boolean;
  thumbnail: string;
  title: string;
  trialMode: boolean;
  trialEnded: boolean;
  onStartTrial: () => void;
  onResetTrial: () => void;
  onTrialEnd: () => void;
}

function VideoPlayer({ purchased, thumbnail, title, trialMode, trialEnded, onStartTrial, onResetTrial, onTrialEnd }: VideoPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [inputUrl, setInputUrl] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (trialMode && !trialEnded) {
      setVideoUrl(TRIAL_VIDEO_URL);
      setCurrentTime(0);
      setPlaying(false);
    }
    if (!trialMode) {
      if (!purchased) {
        setVideoUrl('');
        setCurrentTime(0);
        setPlaying(false);
      }
    }
  }, [trialMode, trialEnded, purchased]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) { videoRef.current.pause(); setPlaying(false); }
    else { videoRef.current.play(); setPlaying(true); }
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const t = videoRef.current.currentTime;
    setCurrentTime(t);
    if (trialMode && !purchased && t >= TRIAL_LIMIT_SECONDS) {
      videoRef.current.pause();
      setPlaying(false);
      onTrialEnd();
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = Number(e.target.value);
    if (videoRef.current) { videoRef.current.currentTime = t; setCurrentTime(t); }
  };

  const handleVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value);
    setVolume(v);
    if (videoRef.current) videoRef.current.volume = v;
    setMuted(v === 0);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const next = !muted;
    setMuted(next);
    videoRef.current.muted = next;
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) { containerRef.current.requestFullscreen(); setFullscreen(true); }
    else { document.exitFullscreen(); setFullscreen(false); }
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const applyUrl = () => {
    if (inputUrl.trim()) { setVideoUrl(inputUrl.trim()); setShowUrlInput(false); setPlaying(false); }
  };

  useEffect(() => {
    const handler = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  if (!purchased && !trialMode) {
    return (
      <div className="relative bg-gray-900 rounded-2xl overflow-hidden mb-6" style={{ aspectRatio: '16/9' }}>
        <img src={thumbnail} alt={title} className="w-full h-full object-cover object-top opacity-40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-green-500/20 border-2 border-green-400/50 rounded-full mb-4">
            <i className="ri-play-circle-line text-green-400 text-3xl"></i>
          </div>
          <p className="text-white font-bold text-lg mb-1">免费试听前3分钟</p>
          <p className="text-white/60 text-sm mb-5">无需购买，立即体验课程内容</p>
          <button
            onClick={onStartTrial}
            className="px-6 py-2.5 bg-green-500 hover:bg-green-400 text-white font-semibold rounded-full cursor-pointer transition-colors whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-play-fill"></i>免费试听
          </button>
        </div>
        <div className="absolute top-4 left-4 bg-green-500 text-white text-xs px-3 py-1 rounded-full font-semibold flex items-center gap-1">
          <i className="ri-gift-line"></i> 免费试听3分钟
        </div>
      </div>
    );
  }

  if (!purchased && trialMode && trialEnded) {
    return (
      <div className="relative bg-gray-900 rounded-2xl overflow-hidden mb-6" style={{ aspectRatio: '16/9' }}>
        <img src={thumbnail} alt={title} className="w-full h-full object-cover object-top opacity-30" />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          <div className="w-14 h-14 flex items-center justify-center bg-amber-500/20 border-2 border-amber-400/50 rounded-full mb-4">
            <i className="ri-time-line text-amber-400 text-2xl"></i>
          </div>
          <p className="text-white font-bold text-lg mb-1">试听已结束</p>
          <p className="text-white/70 text-sm mb-5">购买完整课程，解锁全部内容</p>
          <button
            onClick={onResetTrial}
            className="px-5 py-2.5 bg-white/15 border border-white/30 text-white font-medium rounded-full cursor-pointer transition-colors whitespace-nowrap text-sm"
          >
            <i className="ri-refresh-line mr-1"></i>重新试听
          </button>
        </div>
        <div className="absolute top-4 left-4 bg-amber-500 text-white text-xs px-3 py-1 rounded-full font-semibold">
          试听已结束
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative bg-gray-900 rounded-2xl overflow-hidden mb-6 group" style={{ aspectRatio: '16/9' }}>
      {videoUrl ? (
        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full object-contain"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setPlaying(false)}
          onClick={togglePlay}
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center">
          <img src={thumbnail} alt={title} className="absolute inset-0 w-full h-full object-cover object-top opacity-30" />
          <div className="relative z-10 text-center px-6">
            <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
              <i className="ri-video-add-line text-white text-2xl"></i>
            </div>
            <p className="text-white font-semibold mb-2">输入视频链接开始播放</p>
            <p className="text-white/60 text-xs mb-4">支持 MP4、WebM 等格式的直链地址</p>
            <button onClick={() => setShowUrlInput(true)} className="px-5 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap">
              输入视频链接
            </button>
          </div>
        </div>
      )}

      {videoUrl && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 pb-3 pt-8 opacity-0 group-hover:opacity-100 transition-opacity">
          <input type="range" min={0} max={duration || 100} value={currentTime} onChange={handleSeek} className="w-full h-1 mb-2 cursor-pointer accent-red-500" />
          <div className="flex items-center gap-3">
            <button onClick={togglePlay} className="w-8 h-8 flex items-center justify-center text-white cursor-pointer hover:text-red-400 transition-colors">
              <i className={`${playing ? 'ri-pause-fill' : 'ri-play-fill'} text-lg`}></i>
            </button>
            <span className="text-white text-xs whitespace-nowrap">{formatTime(currentTime)} / {formatTime(duration)}</span>
            <div className="flex items-center gap-1 ml-auto">
              <button onClick={toggleMute} className="w-7 h-7 flex items-center justify-center text-white cursor-pointer hover:text-red-400 transition-colors">
                <i className={`${muted || volume === 0 ? 'ri-volume-mute-line' : 'ri-volume-up-line'} text-sm`}></i>
              </button>
              <input type="range" min={0} max={1} step={0.05} value={muted ? 0 : volume} onChange={handleVolume} className="w-16 h-1 cursor-pointer accent-red-500" />
            </div>
            {purchased && (
              <button onClick={() => setShowUrlInput(true)} className="w-7 h-7 flex items-center justify-center text-white cursor-pointer hover:text-red-400 transition-colors" title="更换视频">
                <i className="ri-links-line text-sm"></i>
              </button>
            )}
            <button onClick={toggleFullscreen} className="w-7 h-7 flex items-center justify-center text-white cursor-pointer hover:text-red-400 transition-colors">
              <i className={`${fullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-sm`}></i>
            </button>
          </div>
        </div>
      )}

      {trialMode && !purchased && (
        <div className="absolute top-3 left-3 right-3 z-10">
          <div className="bg-black/50 rounded-full px-3 py-1.5 flex items-center gap-2">
            <i className="ri-gift-line text-green-400 text-xs"></i>
            <span className="text-white text-xs font-medium">免费试听</span>
            <div className="flex-1 bg-white/20 rounded-full h-1">
              <div className="bg-green-400 h-1 rounded-full transition-all" style={{ width: `${Math.min((currentTime / TRIAL_LIMIT_SECONDS) * 100, 100)}%` }}></div>
            </div>
            <span className="text-white/70 text-xs whitespace-nowrap">{Math.max(0, Math.floor(TRIAL_LIMIT_SECONDS - currentTime))}s 剩余</span>
          </div>
        </div>
      )}

      {videoUrl && !playing && (
        <div className="absolute inset-0 flex items-center justify-center cursor-pointer" onClick={togglePlay}>
          <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors">
            <i className="ri-play-fill text-white text-3xl"></i>
          </div>
        </div>
      )}

      {showUrlInput && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20 p-6">
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm">
            <h4 className="font-bold text-gray-900 mb-3">输入视频链接</h4>
            <p className="text-xs text-gray-400 mb-3">请输入视频文件的直链地址（MP4/WebM格式）</p>
            <input type="url" value={inputUrl} onChange={e => setInputUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && applyUrl()} placeholder="https://example.com/video.mp4" className="w-full bg-gray-50 rounded-xl px-4 py-2.5 text-sm outline-none mb-3" autoFocus />
            <div className="flex gap-2">
              <button onClick={applyUrl} className="flex-1 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap">确认播放</button>
              <button onClick={() => setShowUrlInput(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm rounded-full cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap">取消</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function VideoDetailPage() {
  const { id } = useParams();
  const video = featuredVideos.find(v => v.id === id) || featuredVideos[0];
  const [purchased, setPurchased] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'intro' | 'catalog' | 'reviews'>('intro');
  const [trialMode, setTrialMode] = useState(false);
  const [trialEnded, setTrialEnded] = useState(false);

  const related = featuredVideos.filter(v => v.id !== video.id).slice(0, 4);

  useEffect(() => {
    const schemaId = 'schema-video-detail';
    let el = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = schemaId;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'VideoObject',
          '@id': `${SITE_URL}/videos/${video.id}`,
          name: video.title,
          description: `${video.teacher}主讲，${video.grade}阶段${video.category}课程，时长${video.duration}，${video.students.toLocaleString()}人学习，评分${video.rating}。`,
          thumbnailUrl: video.thumbnail,
          uploadDate: '2026-01-01',
          duration: 'PT30M',
          author: { '@type': 'Person', name: video.teacher },
          inLanguage: 'zh-CN',
          isAccessibleForFree: false,
          offers: {
            '@type': 'Offer',
            price: video.price,
            priceCurrency: 'CNY',
            availability: 'https://schema.org/InStock',
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: '教学视频', item: `${SITE_URL}/videos` },
            { '@type': 'ListItem', position: 3, name: video.title, item: `${SITE_URL}/videos/${video.id}` },
          ],
        },
      ],
    };
    el.textContent = JSON.stringify(schema);
    document.title = `${video.title} - ${video.teacher} | 金榜旺财`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', `${video.title}，${video.teacher}主讲，${video.grade}阶段${video.category}课程，时长${video.duration}，${video.students.toLocaleString()}人学习，评分${video.rating}。金榜旺财精品课程。`);
    return () => { const s = document.getElementById(schemaId); if (s) s.remove(); };
  }, [video]);

  const handleStartTrial = () => {
    setTrialMode(true);
    setTrialEnded(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleResetTrial = () => {
    setTrialMode(false);
    setTrialEnded(false);
  };

  const handleTrialEnd = () => {
    setTrialEnded(true);
  };

  const chapters = [
    { num: 1, title: '课程介绍与学习方法', duration: '8分钟', free: true },
    { num: 2, title: '基础知识回顾与梳理', duration: '12分钟', free: true },
    { num: 3, title: '核心考点精讲（一）', duration: '18分钟', free: false },
    { num: 4, title: '核心考点精讲（二）', duration: '15分钟', free: false },
    { num: 5, title: '真题解析与技巧总结', duration: '20分钟', free: false },
    { num: 6, title: '模拟练习与答疑', duration: '10分钟', free: false },
  ];

  const reviews = [
    { id: '1', name: '高三小明', rating: 5, content: '王老师讲得非常清晰，压轴题的解题思路一下子就明白了，强烈推荐！', time: '3天前', avatar: 'https://public.readdy.ai/ai/img_res/516ee2f168f4e9d9ae45e6c367bc3164.jpg' },
    { id: '2', name: '备考生小李', rating: 5, content: '课程质量很高，老师经验丰富，讲解深入浅出，值得购买！', time: '1周前', avatar: 'https://readdy.ai/api/search-image?query=young%20Chinese%20student%20portrait%2C%20simple%20background&width=40&height=40&seq=rv002&orientation=squarish' },
    { id: '3', name: '农村学生小花', rating: 4, content: '内容很实用，对我这种基础薄弱的同学帮助很大，就是希望能多一些练习题。', time: '2周前', avatar: 'https://readdy.ai/api/search-image?query=young%20Chinese%20girl%20student%20portrait%2C%20warm%20smile&width=40&height=40&seq=rv003&orientation=squarish' },
  ];

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      <Navbar />
      <div className="pt-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Link to="/" className="hover:text-red-600 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/videos" className="hover:text-red-600 cursor-pointer">教学视频</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-600 line-clamp-1">{video.title}</span>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <VideoPlayer
                purchased={purchased}
                thumbnail={video.thumbnail}
                title={video.title}
                trialMode={trialMode}
                trialEnded={trialEnded}
                onStartTrial={handleStartTrial}
                onResetTrial={handleResetTrial}
                onTrialEnd={handleTrialEnd}
              />

              <h1 className="text-2xl font-black text-gray-900 mb-3">{video.title}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-5">
                <span className="flex items-center gap-1"><i className="ri-user-line"></i>{video.teacher}</span>
                <span className="flex items-center gap-1"><i className="ri-time-line"></i>{video.duration}</span>
                <span className="flex items-center gap-1"><i className="ri-group-line"></i>{video.students.toLocaleString()}人学习</span>
                <span className="flex items-center gap-1 text-amber-500"><i className="ri-star-fill"></i>{video.rating}分</span>
                <span className="bg-red-50 text-red-600 text-xs px-2.5 py-1 rounded-full">{video.category}</span>
              </div>

              <div className="flex gap-1 bg-white rounded-full p-1 mb-6 w-fit">
                {[{ key: 'intro', label: '课程介绍' }, { key: 'catalog', label: '课程目录' }, { key: 'reviews', label: `评价(${reviews.length})` }].map(tab => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key as typeof activeTab)}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === tab.key ? 'bg-red-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === 'intro' && (
                <div className="bg-white rounded-2xl p-6">
                  <h3 className="font-bold text-gray-900 mb-3">课程简介</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    本课程由{video.teacher}精心录制，专为{video.grade}学生设计。课程涵盖{video.category}核心考点，通过系统讲解和真题解析，帮助学生快速掌握解题技巧，提升成绩。
                  </p>
                  <h3 className="font-bold text-gray-900 mb-3">你将学到</h3>
                  <ul className="flex flex-col gap-2">
                    {['掌握核心考点和解题思路', '学会高效的学习方法和技巧', '通过真题练习巩固所学知识', '提升应试能力，冲刺高分'].map(item => (
                      <li key={item} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-checkbox-circle-fill text-green-500 flex-shrink-0"></i>{item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {activeTab === 'catalog' && (
                <div className="bg-white rounded-2xl overflow-hidden">
                  {chapters.map(ch => (
                    <div key={ch.num} className={`flex items-center gap-4 px-6 py-4 border-b border-gray-50 last:border-0 ${ch.free || purchased ? 'cursor-pointer hover:bg-gray-50' : 'opacity-60'}`}>
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 text-sm font-bold ${ch.free || purchased ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-400'}`}>{ch.num}</div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{ch.title}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{ch.duration}</p>
                      </div>
                      {ch.free ? <span className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full">免费</span>
                        : purchased ? <i className="ri-play-circle-line text-red-500"></i>
                        : <i className="ri-lock-line text-gray-300"></i>}
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'reviews' && (
                <div className="flex flex-col gap-4">
                  {reviews.map(review => (
                    <div key={review.id} className="bg-white rounded-2xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <img src={review.avatar} alt={review.name} className="w-9 h-9 rounded-full object-cover object-top" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">{review.name}</p>
                          <div className="flex items-center gap-1">
                            {Array.from({ length: review.rating }).map((_, i) => <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>)}
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">{review.time}</span>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">{review.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 sticky top-24">
                <div className="relative rounded-xl overflow-hidden h-40 mb-5">
                  <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/80 rounded-full">
                      <i className="ri-play-fill text-red-600 text-xl"></i>
                    </div>
                  </div>
                </div>

                {purchased ? (
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 text-green-600 mb-3">
                      <i className="ri-checkbox-circle-fill text-xl"></i>
                      <span className="font-bold">已购买</span>
                    </div>
                    <button className="w-full py-3 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap">
                      开始学习
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-3xl font-black text-red-600">¥{video.price}</span>
                      <span className="text-gray-300 line-through text-sm">¥{video.originalPrice}</span>
                      <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">
                        {Math.round((1 - video.price / video.originalPrice) * 100)}%OFF
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mb-4">限时优惠，随时可能恢复原价</p>

                    {!trialMode && !trialEnded && (
                      <div className="bg-green-50 border border-green-200 rounded-xl px-3 py-2.5 mb-4 flex items-center gap-2">
                        <i className="ri-gift-line text-green-600 text-sm flex-shrink-0"></i>
                        <p className="text-green-700 text-xs leading-snug">购买前可免费试听前3分钟，体验课程质量</p>
                      </div>
                    )}
                    {trialEnded && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 mb-4 flex items-center gap-2">
                        <i className="ri-time-line text-amber-600 text-sm flex-shrink-0"></i>
                        <p className="text-amber-700 text-xs leading-snug">试听已结束，购买完整课程继续学习</p>
                      </div>
                    )}

                    <button onClick={() => setShowPayModal(true)} className="w-full py-3.5 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap mb-3 shadow-md hover:shadow-lg">
                      立即购买
                    </button>
                    <button
                      onClick={handleStartTrial}
                      className="w-full py-3 border-2 border-green-300 text-green-600 font-semibold rounded-full cursor-pointer hover:bg-green-50 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <i className="ri-play-circle-line"></i>
                      {trialEnded ? '重新试听' : '免费试听3分钟'}
                    </button>
                  </>
                )}

                <div className="mt-5 flex flex-col gap-2">
                  {[
                    { icon: 'ri-time-line', text: `课程时长：${video.duration}` },
                    { icon: 'ri-file-list-line', text: `共${chapters.length}章节` },
                    { icon: 'ri-infinity-line', text: '永久有效，随时回看' },
                    { icon: 'ri-customer-service-line', text: '专属答疑服务' },
                  ].map(item => (
                    <div key={item.text} className="flex items-center gap-2 text-xs text-gray-500">
                      <i className={`${item.icon} text-gray-400`}></i>{item.text}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-xl font-black text-gray-900 mb-5">相关课程推荐</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map(v => (
                <Link key={v.id} to={`/videos/${v.id}`} className="bg-white rounded-2xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-all block">
                  <div className="h-32 overflow-hidden">
                    <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-3">
                    <p className="text-xs font-bold text-gray-900 line-clamp-2 mb-1">{v.title}</p>
                    <p className="text-red-600 font-bold text-sm">¥{v.price}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showPayModal && (
        <PaymentModal
          videoTitle={video.title}
          price={video.price}
          onClose={() => setShowPayModal(false)}
          onSuccess={() => {
            setPurchased(true);
            setShowPayModal(false);
            setTrialMode(false);
            setTrialEnded(false);
          }}
        />
      )}

      <Footer />
    </div>
  );
}
